import { useMemo } from 'react';
import { Control, useController } from 'react-hook-form';
import { Country, getCountryCallingCode } from 'react-phone-number-input';

import { ContactDetailsFormValues } from '../../types';
import { isBusinessContactEmailRequired } from '../../utils';
import {
  createMaxLengthValidator,
  createPatternValidator,
  createRequiredPhoneValidator,
  createRequiredValidator,
} from '../../validators';
import regexExpressions from '@/constants/regexExpressions';

const useControllers = (
  control: Control<ContactDetailsFormValues, any, ContactDetailsFormValues>,
  entityType: string,
  phoneCountry?: Country
) => {
  const phoneCountryCode = useMemo(
    () => (phoneCountry ? getCountryCallingCode(phoneCountry) : ''),
    [phoneCountry]
  );
  const {
    field: { value: email, onChange: setEmail, onBlur: validateEmail },
    fieldState: { error: emailError },
  } = useController({
    control,
    name: 'email',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please enter Support Email Address'
        ),
        isValidMaxLength: createMaxLengthValidator(100),
        isValidEmail: createPatternValidator(
          regexExpressions.email,
          'Enter in the format name@example.com'
        ),
      },
    },
  });
  const {
    field: { value: phone, onChange: setPhone, onBlur: validatePhone },
    fieldState: { error: phoneError },
  } = useController({
    control,
    name: 'phone',
    rules: {
      validate: {
        isRequired: createRequiredPhoneValidator(
          phoneCountryCode,
          'Please enter Support Phone Number'
        ),
        ...(phoneCountry === 'IN'
          ? { withValidMaxLength: createMaxLengthValidator(13) }
          : {}),
      },
    },
  });
  const {
    field: {
      value: businessContactEmail,
      onChange: setBusinessContactEmail,
      onBlur: validateBusinessContactEmail,
    },
    fieldState: { error: businessContactEmailError },
  } = useController({
    control,
    name: 'businessContactEmail',
    rules: {
      validate: {
        ...(isBusinessContactEmailRequired(entityType)
          ? {
              isRequired: createRequiredValidator(
                'Please enter Business Email Address'
              ),
            }
          : {}),
        isValidMaxLength: createMaxLengthValidator(100),
        isValidEmail: createPatternValidator(
          regexExpressions.email,
          'Enter in the format name@example.com'
        ),
      },
    },
  });

  return {
    email,
    setEmail,
    validateEmail,
    emailError,
    phone,
    setPhone,
    validatePhone,
    phoneError,
    businessContactEmail,
    setBusinessContactEmail,
    validateBusinessContactEmail,
    businessContactEmailError,
  };
};

export default useControllers;
